import { useEffect, useState } from 'react';
import type { MediaItem, QrPosition, Transition } from '@wallflower/shared';

const TRANSITION_MS = 900;

interface Layer {
  key: string;
  item: MediaItem;
}

interface Props {
  current: MediaItem | null;
  upcoming: MediaItem[];
  /** Changes on every advance, so a repeat of the same item still re-mounts. */
  slideKey: number;
  transition: Transition;
  slideDurationMs: number;
  showCaption: boolean;
  /** Where the QR card sits, so the caption can keep out of its way. */
  qrPosition: QrPosition;
  /** Raise the caption so the message strip doesn't run under it. */
  liftAboveTicker?: boolean;
  onEnded: () => void;
}

export default function SlideshowStage({
  current,
  upcoming,
  slideKey,
  transition,
  slideDurationMs,
  showCaption,
  qrPosition,
  liftAboveTicker = false,
  onEnded,
}: Props) {
  // The outgoing item stays mounted for the length of the transition so the
  // screen never flashes black between slides.
  const [layers, setLayers] = useState<Layer[]>([]);

  useEffect(() => {
    if (!current) {
      setLayers([]);
      return;
    }
    const key = `${current.id}-${slideKey}`;
    setLayers((prev) => [...prev.slice(-1), { key, item: current }]);
    const timer = setTimeout(() => {
      setLayers((prev) => prev.filter((layer) => layer.key === key));
    }, TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [current, slideKey]);

  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      {layers.map((layer, index) => {
        const isTop = index === layers.length - 1;
        return (
          <div
            key={layer.key}
            className="absolute inset-0"
            style={{
              zIndex: isTop ? 2 : 1,
              opacity: isTop ? 1 : 0,
              transition: `opacity ${TRANSITION_MS}ms ease-in-out`,
              animation: isTop ? animationFor(transition, slideDurationMs) : undefined,
            }}
          >
            <Slide item={layer.item} active={isTop} onEnded={onEnded} />
          </div>
        );
      })}

      {showCaption && current?.uploaderName ? (
        <p
          key={current.id}
          className={`absolute z-10 rounded-full px-[1.1vw] py-[0.55vw] text-[1.35vw] text-chalk/90 backdrop-blur-md ${
            qrPosition === 'bottom-left' ? 'right-[2.5vw]' : 'left-[2.5vw]'
          } ${liftAboveTicker ? 'bottom-[10vh]' : 'bottom-[3vh]'}`}
          style={{ background: 'rgba(16,13,23,0.55)', fontFamily: 'var(--font-display)' }}
        >
          {current.uploaderName}
        </p>
      ) : null}

      {/* Warm the browser cache for what's next so transitions never hitch. */}
      <div className="pointer-events-none absolute h-px w-px opacity-0">
        {upcoming.map((item) =>
          item.kind === 'image' ? (
            <img key={item.id} src={item.url} alt="" decoding="async" />
          ) : (
            <video key={item.id} src={item.url} preload="auto" muted />
          ),
        )}
      </div>
    </div>
  );
}

function animationFor(transition: Transition, slideDurationMs: number): string {
  if (transition === 'slide') return `wf-slide-in ${TRANSITION_MS}ms ease-out both`;
  if (transition === 'kenburns') {
    // Two animations on the one layer: the zoom runs for the whole slide, the
    // fade only for the handover, so a ken-burns slide cross-fades as well.
    // They touch different properties (transform, opacity) and don't fight.
    return (
      `wf-kenburns ${slideDurationMs + TRANSITION_MS}ms linear both, ` +
      `wf-fade-in ${TRANSITION_MS}ms ease-in-out both`
    );
  }
  return `wf-fade-in ${TRANSITION_MS}ms ease-in-out both`;
}

function Slide({
  item,
  active,
  onEnded,
}: {
  item: MediaItem;
  active: boolean;
  onEnded: () => void;
}) {
  if (item.kind === 'video') {
    return (
      <video
        className="absolute inset-0 h-full w-full object-contain"
        src={item.url}
        poster={item.thumbUrl}
        autoPlay={active}
        muted
        playsInline
        onEnded={active ? onEnded : undefined}
      />
    );
  }

  return (
    <>
      {/* Portrait phone photos on a 16:9 TV get their own blurred surround
          instead of two black bars. */}
      <div
        className="absolute inset-0 scale-110 bg-cover bg-center blur-3xl brightness-[0.35]"
        style={{ backgroundImage: `url(${item.thumbUrl})` }}
        aria-hidden
      />
      <img
        className="absolute inset-0 h-full w-full object-contain"
        src={item.url}
        alt={item.uploaderName ? `Photo from ${item.uploaderName}` : 'Photo from a guest'}
        decoding="async"
      />
    </>
  );
}
